import { v4 as uuidV4 } from 'uuid';
export default {
  name: 'PersonaSection',
  props: {
    memberProfile: {
      type: Object,
      required: true
    },
    standalone: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      persona: null,
      isPersonaOpen: false,
      isLoading: false,
      inquiryOnHold: false,
      requestList: [],
      requestHash: null
    };
  },
  head() {
    return {
      script: [{
        hid: 'persona',
        src: 'https://cdn.withpersona.com/dist/persona-v4.2.0.js',
        callback: async () => {
          await this.loadPersona();
          // only auto-open when the load actually produced a client - a terminal
          // inquiry leaves this.persona null and must not trigger a second load
          if (this.standalone && this.persona) this.openPersona();
        }
      }]
    };
  },
  computed: {
    showLoadingOverlay() {
      return !this.persona && !this.inquiryOnHold || this.isPersonaOpen || this.isLoading;
    }
  },
  watch: {
    'persona.isOpen': {
      deep: true,
      handler(val) {
        this.isPersonaOpen = val;
      }
    },
    showLoadingOverlay: {
      immediate: true,
      handler(val) {
        this.$emit('loading', val);
      }
    }
  },
  methods: {
    async loadPersona() {
      if (!window.Persona) {
        throw new Error('Persona script failed to load');
      }
      try {
        var _this$memberProfile9, _options, _options2, _options3;
        let personaAccount = this.memberProfile.persona;
        if (!personaAccount) {
          var _this$memberProfile, _personaAccount, _personaAccount2;
          // create or fetch Persona account
          const {
            data
          } = await this.$axios.get('/api/members/persona/account');
          personaAccount = data;
          console.log(`[persona] mid#${(_this$memberProfile = this.memberProfile) === null || _this$memberProfile === void 0 ? void 0 : _this$memberProfile.id}: fetched persona account with inquiry_id ${(_personaAccount = personaAccount) === null || _personaAccount === void 0 ? void 0 : _personaAccount.inquiry_id} and status ${(_personaAccount2 = personaAccount) === null || _personaAccount2 === void 0 ? void 0 : _personaAccount2.inquiry_status}`);
        } else {
          var _this$memberProfile2, _personaAccount3, _personaAccount4;
          console.log(`[persona] mid#${(_this$memberProfile2 = this.memberProfile) === null || _this$memberProfile2 === void 0 ? void 0 : _this$memberProfile2.id}: using cached memberProfile.persona with inquiry_id ${(_personaAccount3 = personaAccount) === null || _personaAccount3 === void 0 ? void 0 : _personaAccount3.inquiry_id} and status ${(_personaAccount4 = personaAccount) === null || _personaAccount4 === void 0 ? void 0 : _personaAccount4.inquiry_status}`);
        }

        // declined/canceled/needs_review are intentional kyc outcomes (incl. manual duplicate
        // cancels in persona) - never auto-start a new inquiry from them. re-fetch once in case
        // staff already re-requested docs and a fresh inquiry exists server-side
        if (['declined', 'canceled', 'needs_review'].includes(personaAccount.inquiry_status)) {
          const {
            data: freshAccount
          } = await this.$axios.get('/api/members/persona/account');

          // only adopt the refreshed account on a known status - anything unknown stays on hold
          const adoptableStatuses = ['created', 'pending', 'expired', 'completed', 'failed', 'approved'];
          if (freshAccount && adoptableStatuses.includes(freshAccount.inquiry_status)) {
            personaAccount = freshAccount;
          } else {
            var _this$memberProfile3, _personaAccount5, _personaAccount6;
            console.log(`[persona] mid#${(_this$memberProfile3 = this.memberProfile) === null || _this$memberProfile3 === void 0 ? void 0 : _this$memberProfile3.id}: inquiry ${(_personaAccount5 = personaAccount) === null || _personaAccount5 === void 0 ? void 0 : _personaAccount5.inquiry_id} is ${(_personaAccount6 = personaAccount) === null || _personaAccount6 === void 0 ? void 0 : _personaAccount6.inquiry_status}, not auto-starting a new one - waiting on staff follow-up`);
            if (this.standalone) {
              this.$emit('cancel');
              return;
            }
            this.inquiryOnHold = true;
            return;
          }
        }
        let options = {};
        if (['completed', 'failed', 'approved'].includes(personaAccount.inquiry_status) && !(this.memberProfile && this.memberProfile.document_portal.kyc_required_docs && this.memberProfile.document_portal.kyc_required_docs.length)) {
          var _this$memberProfile4, _personaAccount7, _personaAccount8;
          console.log(`[persona] mid#${(_this$memberProfile4 = this.memberProfile) === null || _this$memberProfile4 === void 0 ? void 0 : _this$memberProfile4.id}: ${(_personaAccount7 = personaAccount) === null || _personaAccount7 === void 0 ? void 0 : _personaAccount7.inquiry_id} already complete, status: ${(_personaAccount8 = personaAccount) === null || _personaAccount8 === void 0 ? void 0 : _personaAccount8.inquiry_status}`);
          this.$emit('complete');
          return;
        } else if (['created', 'pending', 'expired'].includes(personaAccount.inquiry_status)) {
          var _this$memberProfile5, _personaAccount9, _personaAccount0;
          console.log(`[persona] mid${(_this$memberProfile5 = this.memberProfile) === null || _this$memberProfile5 === void 0 ? void 0 : _this$memberProfile5.id}: resuming inquiry ${(_personaAccount9 = personaAccount) === null || _personaAccount9 === void 0 ? void 0 : _personaAccount9.inquiry_id}, status: ${(_personaAccount0 = personaAccount) === null || _personaAccount0 === void 0 ? void 0 : _personaAccount0.inquiry_status}`);
          await this.getKycRequest();
          const {
            data: session
          } = await this.$axios.post('/api/members/persona/session', {
            inquiry_id: personaAccount.inquiry_id
          });
          if (session.completed) {
            var _this$memberProfile6, _personaAccount1;
            // handle cases where the inquiry is complete but the persona_accounts table doesn't reflect that (ex. if you close the window at the very end of the Persona flow)
            console.log(`[persona] mid#${(_this$memberProfile6 = this.memberProfile) === null || _this$memberProfile6 === void 0 ? void 0 : _this$memberProfile6.id}: inquiry ${(_personaAccount1 = personaAccount) === null || _personaAccount1 === void 0 ? void 0 : _personaAccount1.inquiry_id} already complete`);
            this.$emit('complete');
            return;
          }
          options = {
            inquiryId: session.data.id,
            sessionToken: session.meta.sessionToken || session.meta['session-token']
          };
        } else {
          var _this$memberProfile7, _personaAccount10;
          console.log(`[persona] mid#${(_this$memberProfile7 = this.memberProfile) === null || _this$memberProfile7 === void 0 ? void 0 : _this$memberProfile7.id}: starting new inquiry, status: ${(_personaAccount10 = personaAccount) === null || _personaAccount10 === void 0 ? void 0 : _personaAccount10.inquiry_status}`);
          await this.getKycRequest();
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'persona_template_set',
            template_id: this.$config.templateId
          });
          options = {
            accountId: personaAccount.account_id,
            templateId: this.$config.templateId,
            fields: {
              // keys are from personaToExtraFlag in extra-api KYCRequest model
              docIdSelfie: ['VERIFY_DOB', 'VERIFY_NAME', 'VERIFY_PERSON', 'id', 'selfie', 'passport'].some(key => this.requestList.includes(key)),
              docAddress: ['VERIFY_ADDRESS', 'address'].some(key => this.requestList.includes(key)),
              docBank: ['VERIFY_ACCOUNT_OWNERSHIP', 'VERIFY_BUSINESS_OWNERSHIP', 'bank', 'business'].some(key => this.requestList.includes(key)),
              docSsn: ['identity', 'VERIFY_SSN'].some(key => this.requestList.includes(key))
            }
          };
          if (options.fields.docBank) {
            options.fields.accountNumberLastFour = this.memberProfile.bank_account.mask;
            // get routing from Plaid--not included in original connection through SDK
            try {
              const numbers = await this.$axios.post('/api/members/plaid/need-routing', {
                mid: this.memberProfile.id
              });
              options.fields.routingNumber = numbers.data.routing;
            } catch (err) {
              var _this$memberProfile8;
              console.log(`[persona] mid#${(_this$memberProfile8 = this.memberProfile) === null || _this$memberProfile8 === void 0 ? void 0 : _this$memberProfile8.id}: error getting routing number`, err);
              this.$emit('relinkPlaid', err);
              return;
            }
          } else {
            options.fields.accountNumberLastFour = null;
            options.fields.routingNumber = null;
          }
        }
        console.log(`[persona] mid#${(_this$memberProfile9 = this.memberProfile) === null || _this$memberProfile9 === void 0 ? void 0 : _this$memberProfile9.id}: inquiry options`, options, `bank request? ${(_options = options) === null || _options === void 0 || (_options = _options.fields) === null || _options === void 0 ? void 0 : _options.docBank}
					account number last 4 present? ${!!((_options2 = options) !== null && _options2 !== void 0 && (_options2 = _options2.fields) !== null && _options2 !== void 0 && _options2.accountNumberLastFour)}
					routing number present? ${!!((_options3 = options) !== null && _options3 !== void 0 && (_options3 = _options3.fields) !== null && _options3 !== void 0 && _options3.routingNumber)}`);
        this.persona = new window.Persona.Client({
          ...options,
          environment: this.$config.environment === 'production' ? 'production' : 'sandbox',
          onEvent: async (eventName, metadata) => {
            var _this$memberProfile0;
            console.log(`[persona] mid#${(_this$memberProfile0 = this.memberProfile) === null || _this$memberProfile0 === void 0 ? void 0 : _this$memberProfile0.id}: handling event ${eventName}...`, metadata);
            if (eventName === 'start') {
              var _this$memberProfile1;
              await this.$axios.put('/api/members/persona/account', {
                inquiry_id: metadata.inquiryId,
                inquiry_status: 'created'
              });
              console.log(`[persona] mid#${(_this$memberProfile1 = this.memberProfile) === null || _this$memberProfile1 === void 0 ? void 0 : _this$memberProfile1.id}: event started!`);
            }
            // matadata.name = verification_government_id & path = .../select is first page after "Begin Verifying" btn is clicked
            if (eventName === 'page-change' && metadata.path === '/inquiry/government-id/select') {
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({
                event: 'document_upload_started'
              });
              const eventID = uuidV4();
              const eventName = 'Document Upload Started';
              this.$fb.query('trackCustom', eventName, null, {
                eventID
              });
              this.$axios.$post('/api/fbevent', {
                eventName,
                member: {
                  ...this.memberProfile,
                  external_id: this.$cookies.get('ajs_anonymous_id')
                },
                eventId: eventID
              }).catch(err => {
                var _this$memberProfile10;
                console.log(`[fbEvent] ${eventName} CAPI failed for mid: ${(_this$memberProfile10 = this.memberProfile) === null || _this$memberProfile10 === void 0 ? void 0 : _this$memberProfile10.id} with error: ${err}`);
              });
            }
          },
          onCancel: () => {
            this.$emit('cancel');
          },
          onComplete: async ({
            inquiryId,
            status
          }) => {
            var _this$memberProfile11, _this$memberProfile12;
            console.log(`[persona] mid#${(_this$memberProfile11 = this.memberProfile) === null || _this$memberProfile11 === void 0 ? void 0 : _this$memberProfile11.id}: completing inquiry ${inquiryId} with status ${status}...`);
            this.toggleIsLoading(true);
            await this.$axios.put('/api/members/persona/account', {
              inquiry_id: inquiryId,
              inquiry_status: status,
              kyc_request_hash: this.requestHash
            });
            console.log(`[persona] mid#${(_this$memberProfile12 = this.memberProfile) === null || _this$memberProfile12 === void 0 ? void 0 : _this$memberProfile12.id}: completed inquiry ${inquiryId} with status ${status}!`);
            this.$emit('complete');
            this.toggleIsLoading(false);
            this.$analyticsHub.track('Document Upload Completed', {
              event_datetime: this.memberProfile.updated_at,
              member_id: this.memberProfile.id,
              user_id: this.memberProfile.extra_user_id
            });
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'document_upload_completed'
            });
            const eventID = uuidV4();
            const eventName = 'Document Upload Completed';
            this.$fb.query('trackCustom', eventName, null, {
              eventID
            });
            this.$axios.$post('/api/fbevent', {
              eventName,
              member: {
                ...this.memberProfile,
                external_id: this.$cookies.get('ajs_anonymous_id')
              },
              eventId: eventID
            }).catch(err => {
              var _this$memberProfile13;
              console.log(`[fbEvent] ${eventName} CAPI failed for mid: ${(_this$memberProfile13 = this.memberProfile) === null || _this$memberProfile13 === void 0 ? void 0 : _this$memberProfile13.id} with error: ${err}`);
            });
          },
          onError: this.handleError
        });
      } catch (error) {
        this.handleError(error);
      }
    },
    async openPersona() {
      var _this$memberProfile15;
      if (!this.persona) {
        await this.loadPersona();
      }

      // inquiry on hold (declined/canceled) - nothing to open
      if (!this.persona) {
        return;
      }
      if (this.isPersonaOpen || this.persona.isOpen) {
        var _this$memberProfile14;
        console.warn(`[persona] mid#${(_this$memberProfile14 = this.memberProfile) === null || _this$memberProfile14 === void 0 ? void 0 : _this$memberProfile14.id}: Persona is already open`);
        return;
      }
      this.persona.open();
      console.log(`[persona] mid#${(_this$memberProfile15 = this.memberProfile) === null || _this$memberProfile15 === void 0 ? void 0 : _this$memberProfile15.id} opening Persona`);
      this.$analyticsHub.track('Document Upload Started', {
        event_datetime: this.memberProfile.updated_at,
        member_id: this.memberProfile.id,
        user_id: this.memberProfile.extra_user_id
      });
    },
    async getKycRequest() {
      try {
        const kycRequest = await this.$axios.$get('/api/members/persona/kyc');
        this.requestHash = kycRequest.hash || null;
        this.requestList = kycRequest && kycRequest.kyc_required_docs || !(this.memberProfile.personaAccount && this.memberProfile.personaAccount.inquiry_id) && this.memberProfile.fraud_check_data && this.memberProfile.fraud_check_data.validated_params && this.memberProfile.fraud_check_data.validated_params.request_list || [];
      } catch (error) {
        var _this$memberProfile16;
        console.error(`[persona] mid#${(_this$memberProfile16 = this.memberProfile) === null || _this$memberProfile16 === void 0 ? void 0 : _this$memberProfile16.id}: getKycRequest error`);
        this.handleError(error);
      }
    },
    toggleIsLoading(bool) {
      this.isLoading = bool;
    },
    handleError(error) {
      var _this$memberProfile17;
      console.error(`[persona] [error] mid#${(_this$memberProfile17 = this.memberProfile) === null || _this$memberProfile17 === void 0 ? void 0 : _this$memberProfile17.id}`, error);
      this.$emit('error', error);
    }
  }
};